import { Dialog, Icon, TooltipHost } from "@fluentui/react";
import { IFieldConfig } from "components/form/types";
import { Form, Formik } from "formik";
import { useAddVehiclePremium } from "hooks/data/mutations/useAddVehiclePremium";
import { useInsertVehiclePremium } from "hooks/data/mutations/useInsertVehiclePremium";
import { useSwitchVehicleHullPremiumType } from "hooks/data/mutations/useSwitchVehicleHullPremiumType";
import { useGetFleetInsurerDefaultSettings } from "hooks/data/queries/getFleetInsurerDefaultSettings";
import usePhraseActivationStatus from "hooks/usePhraseActivationStatus";
import moment from "moment";
import { getInsurerBranchOffice } from "pages/fleet-form/FleetForm/tabs/InsurerDefaultSettings/utils";
import useActionConfig from "pages/fleet-form/hooks/useActionConfg";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { useTheme } from "styled-components";
import { IStyledTheme } from "theme/types";
import { removeEmpty } from "utils/utils";
import * as yup from "yup";
import { RefProvider } from "../../../../contexts/RefProvider";
import { useSelector } from "../../../../store/hooks";
import {
  closeDialog,
  setDialogDefaultValueOperation,
  setDialogInsurerSettingsOperation,
  setOperationRetVal,
} from "../../../../store/timeline";
import {
  ETimelineExtraDataLineSpecial,
  IFleetInsurerDefaultSetting,
  PremiumType,
  TimelineDialogType,
} from "../../../../types/types";
import { setYupLocale } from "../../../../utils/setYupLocale";
import { toast } from "../../../FluentToast";
import { FormSpinner } from "../../../FormSpinner";
import { transformForServer } from "../../../form/transformForServer";
import { Flex, HSpace, StyledPrimaryButton } from "../../../styled";
import { AddPremiumDialogContent } from "../AddPremiumDialogContent";
import { addPremiumDialogConfig } from "../addPremiumDialogConfig";
import { validationObjectConfig } from "../addPremiumDialogValidation";
import {
  getOneTimePaymentMethodId,
  isPremiumExpiryDateShown,
} from "../premiumExpiryDate";
import { transformForForm } from "../transformForForm";
import { getRequestedCoverageFrom } from "pages/vehicle/FinanceTab/FinanceForm/requestedLinesHelpers";

export type TSetDefaultSettingValues = {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched?: (field: string, value: boolean) => void;
  values: any;
  insurer?: {
    label: string;
    value: number | string;
  };
  noToastMsg?: boolean;
  mustHaveInsurer?: boolean;
  isHullPremium?: boolean;
};

export const AddPremiumDialog = () => {
  const theme = useTheme() as IStyledTheme;
  const { fleetId } = useParams() as any;

  const [didTrySubmit, setDidTrySubmit] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const timelineAppSettings = useSelector(
    (s) => s.timeline.timelineAppSettings,
  );
  const { paymentMeanOptions, paymentMethodOptions } =
    useActionConfig(timelineAppSettings);
  const appSettings = useSelector((s) => s.timeline.timelineAppSettings);
  const isAustria = appSettings?.COUNTRY_CODE === "Country.AT";

  const item = useSelector((s) => s.timeline.dialog.item);
  const premiumTypesTaxonomy = useSelector(
    (s) => s.taxonomy.PremiumType.byCode,
  );
  const insurerList = useSelector((s) => s.vehicle.insurerList);
  const type = useSelector((s) => s.timeline.dialog.type);

  const config = useMemo(() => {
    if (!appSettings) return;
    if (appSettings?.HAS_GREENCARD_EXPIRY_DATE === false) {
      const filteredConfig = Object.keys(addPremiumDialogConfig).reduce(
        (acc, key) => {
          if (key !== "greenCardExpiryDate") {
            acc[key] = addPremiumDialogConfig[key];
          }
          return acc;
        },
        {} as IFieldConfig,
      );
      return filteredConfig;
    }
    return addPremiumDialogConfig;
  }, [appSettings]);

  const insurerOptions = insurerList
    ? insurerList.map((tax) => {
        return {
          value: tax.insurerInternalNumber,
          label: tax.insurerName,
        };
      })
    : [];

  const { data: fleetInsurerDefaultSettings } =
    useGetFleetInsurerDefaultSettings(fleetId);

  const {
    mutate: onInsertVehiclePremium,
    isLoading: insertVehiclePremiumInProgress,
  } = useInsertVehiclePremium();

  const {
    mutate: onAddVehiclePremium,
    isLoading: addVehiclePremiumInProgress,
  } = useAddVehiclePremium();

  const {
    mutate: onSwitchVehicleHullPremiumType,
    isLoading: switchVehicleHullPremiumTypeInProgress,
  } = useSwitchVehicleHullPremiumType();

  const mainMtplInsurerPartnerNumber = useSelector(
    (s) => s.vehicle.fleet.mainMtplInsurerPartnerNumber,
  );

  const mainHullInsurerPartnerNumber = useSelector(
    (s) => s.vehicle.fleet.mainHullInsurerPartnerNumber,
  );
  const mainFleetLegalInsurerPartnerNumber = useSelector(
    (s) => s.vehicle.fleet.mainFleetLegalInsurerPartnerNumber,
  );
  const mainHullOnLandInsurerPartnerNumber = useSelector(
    (s) => s.vehicle.fleet.mainHullOnLandInsurerPartnerNumber,
  );
  const mainMachineryBreakdownInsurerPartnerNumber = useSelector(
    (s) => s.vehicle.fleet.mainMachineryBreakdownInsurerPartnerNumber,
  );
  const premiumTypeKeyItem = useSelector(
    (s) => s.timeline.dialog.item.domain.value,
  );

  const phraseActivated = usePhraseActivationStatus().activated;
  const date = useSelector((s) => s.timeline.dialog.date);

  const insurerPartnerNumber = useSelector(
    (s) => s.timeline.dialog.item.associatedObject?.insurerPartnerNumber,
  );
  const isLoading =
    insertVehiclePremiumInProgress ||
    addVehiclePremiumInProgress ||
    switchVehicleHullPremiumTypeInProgress;
  const premiumTypeKey = premiumTypeKeyItem;
  const premiumTypeValue = premiumTypesTaxonomy[premiumTypeKey]?.id;

  const isMtplPremiumType = premiumTypeKey === PremiumType.MTPL;
  const isHullPremiumType = premiumTypeKey === PremiumType.HULL;

  const isFleetLegalProRataPremiumType =
    premiumTypeKey === PremiumType.FLEETLEGAL_PRORATA;
  const isFleetLegalClosingDatePremiumType =
    premiumTypeKey === PremiumType.FLEETLEGAL_CLOSINGDATE;

  const isMachineryBreakdownProRataPremiumType =
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_PRORATA;
  const isMachineryBreakdownClosingDatePremiumType =
    premiumTypeKey === PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE;

  const isHullOnLandProRataPremiumType =
    premiumTypeKey === PremiumType.HULL_ON_LAND_PRORATA;
  const isHullOnLandClosingDatePremiumType =
    premiumTypeKey === PremiumType.HULL_ON_LAND_CLOSINGDATE;

  const isHorsePowerPremiumType = premiumTypeKey === PremiumType.HORSE_POWER;
  const isLegalPremiumType = premiumTypeKey === PremiumType.LEGAL;
  const isAccidentPremiumType = premiumTypeKey === PremiumType.ACCIDENT;
  const isAssistancePremiumType = premiumTypeKey === PremiumType.ASSISTANCE;

  const vehicle = useSelector((s) => s.vehicle.vehicle);

  const taxonomy = useSelector((s) => s.taxonomy);

  const dueDay = useSelector((s) => s.vehicle.fleet.dueDay);
  const dueMonth = useSelector((s) => s.vehicle.fleet.dueMonth);

  // policy numbers are pre-populated from the fleet only for real fleets;
  // the fleet policy number is the fallback when the premium type specific policy number is not defined
  const isRealFleet = useSelector((s) => !!s.vehicle.fleet.isRealFleet);
  const fleetPolicyNumber = useSelector((s) =>
    isRealFleet ? s.vehicle.fleet.fleetPolicyNumber : null,
  );
  const fleetLegalPolicyNumber = useSelector((s) =>
    isRealFleet
      ? s.vehicle.fleet.fleetLegalPolicyNumber ||
        s.vehicle.fleet.fleetPolicyNumber
      : null,
  );
  const machineryBreakdownPolicyNumber = useSelector((s) =>
    isRealFleet
      ? s.vehicle.fleet.machineryBreakdownPolicyNumber ||
        s.vehicle.fleet.fleetPolicyNumber
      : null,
  );
  const hullOnLandPolicyNumber = useSelector((s) =>
    isRealFleet
      ? s.vehicle.fleet.hullOnLandPolicyNumber ||
        s.vehicle.fleet.fleetPolicyNumber
      : null,
  );

  const rowVersion = useSelector((s) => s.vehicle.vehicle?.rowVersion);

  const isInsertOperation = type === TimelineDialogType.INSERT_PREMIUM;
  // Hull <-> Hull-Basic conversion: the dialog is opened on the segment of the current hull type
  // and creates a premium of the other one (the backend cancels the current premium on the same date)
  const isSwitchOperation = type === TimelineDialogType.SWITCH_HULL_TYPE;
  const switchTargetIsBasic =
    isSwitchOperation &&
    item?.extraData?.lineSpecial !== ETimelineExtraDataLineSpecial.BASIC;

  const hasPremiumHistory = vehicle?.vehiclePremiumSegmentHistoryData?.some(
    (item) => item.premiumTypeCode === premiumTypeValue,
  );

  const vehicleRegistrationDate = vehicle?.registrationDate;

  const schema = useMemo(() => {
    setYupLocale(t);
    let yupObject = validationObjectConfig(
      premiumTypeKey,
      t,
      vehicle.isTradeLicensePlate,
      isInsertOperation,
      appSettings,
      getOneTimePaymentMethodId(taxonomy),
    );
    return yup.object(yupObject);
  }, [
    t,
    premiumTypeKey,
    isInsertOperation,
    vehicle.isTradeLicensePlate,
    taxonomy,
  ]);

  const setDefaultSettingValues = async ({
    setFieldValue,
    values,
    insurer,
    noToastMsg = true,
    mustHaveInsurer = false,
    isHullPremium = true,
    setFieldTouched,
  }: TSetDefaultSettingValues) => {
    // if (!isMtplPremiumType && !isHullBasicType && !isHullPremiumType) return;
    if (mustHaveInsurer && !insurer) return;

    const insurerInternalNumberByPremiumType = (() => {
      if (insurer) {
        return Number(insurer.value);
      }
      if (isMtplPremiumType) {
        return Number(mainMtplInsurerPartnerNumber);
      }
      if (isHullPremiumType) {
        return Number(mainHullInsurerPartnerNumber);
      }
      if (isFleetLegalProRataPremiumType) {
        return Number(mainFleetLegalInsurerPartnerNumber);
      }
      if (isMachineryBreakdownProRataPremiumType) {
        return Number(mainMachineryBreakdownInsurerPartnerNumber);
      }
      if (isHullOnLandProRataPremiumType) {
        return Number(mainHullOnLandInsurerPartnerNumber);
      }
      if (
        isLegalPremiumType ||
        isAccidentPremiumType ||
        isAssistancePremiumType
      ) {
        // These bundled coverages have no dedicated main insurer; inherit the
        // insurer from the MTPL premium active at the selected valid-from date.
        const mtplId = taxonomy.PremiumType.byCode[PremiumType.MTPL]?.id;
        const checkDate = values?.validFromDate
          ? new Date(values.validFromDate)
          : null;
        const activeMtplSegment =
          checkDate &&
          vehicle?.vehiclePremiumSegmentHistoryData?.find((seg) => {
            if (seg.premiumTypeCode !== mtplId) return false;
            const from = new Date(seg.validFromDate);
            const to = seg.validToDate ? new Date(seg.validToDate) : null;
            return from <= checkDate && (to === null || to >= checkDate);
          });
        return activeMtplSegment?.insurerPartnerNumber != null
          ? Number(activeMtplSegment.insurerPartnerNumber)
          : null;
      }
      return null;
    })();

    const insurerPartnerNumberByPremiumType = insurerList.find(
      (insurer) =>
        insurer.insurerInternalNumber === insurerInternalNumberByPremiumType,
    )?.insurerInternalNumber;

    let selectedFleetInsurerDefaultSettings: IFleetInsurerDefaultSetting[] =
      (() => {
        if (
          (isMtplPremiumType ||
            isHullPremiumType ||
            isFleetLegalProRataPremiumType ||
            isMachineryBreakdownProRataPremiumType ||
            isHullOnLandProRataPremiumType ||
            isHorsePowerPremiumType ||
            isLegalPremiumType ||
            isAccidentPremiumType ||
            isAssistancePremiumType) &&
          insurerInternalNumberByPremiumType
        ) {
          return fleetInsurerDefaultSettings.filter(
            (setting) =>
              setting.insurerPartnerNumber ===
              insurerInternalNumberByPremiumType,
          );
        }
        return [];
      })();
    if (
      values.insurerGroupPolicyNumber &&
      values.insurerGroupPolicyNumber !== null
    ) {
      selectedFleetInsurerDefaultSettings =
        selectedFleetInsurerDefaultSettings.filter(
          (ds) =>
            ds.insurerGroupPolicyNumber === null ||
            ds.insurerGroupPolicyNumber ===
              values.insurerGroupPolicyNumber.value,
        );
    } else {
      selectedFleetInsurerDefaultSettings =
        selectedFleetInsurerDefaultSettings.filter(
          (ds) =>
            ds.insurerGroupPolicyNumber === undefined ||
            ds.insurerGroupPolicyNumber === null,
        );
    }
    // Prefer settings whose premium type matches this dialog's premium type;
    // fall back to settings with no premium type set (reserve) when none match.
    const settingsMatchingPremiumType =
      selectedFleetInsurerDefaultSettings.filter(
        (ds) => ds.premiumTypeCode === premiumTypeValue,
      );
    selectedFleetInsurerDefaultSettings =
      settingsMatchingPremiumType.length > 0
        ? settingsMatchingPremiumType
        : selectedFleetInsurerDefaultSettings.filter(
            (ds) =>
              ds.premiumTypeCode === undefined || ds.premiumTypeCode === null,
          );
    selectedFleetInsurerDefaultSettings.sort((a, b) => {
      if (
        a.insurerGroupPolicyNumber == null &&
        b.insurerGroupPolicyNumber != null
      ) {
        return 1; // a should come after b
      } else if (
        a.insurerGroupPolicyNumber != null &&
        b.insurerGroupPolicyNumber == null
      ) {
        return -1; // a should come before b
      } else {
        return 0; // keep the original order between two non-null or two null values
      }
    });

    const selectedFleetInsurerDefaultSetting =
      selectedFleetInsurerDefaultSettings[0];

    const selectedInsurer = insurerOptions.find(
      (option) => option.value === insurerPartnerNumberByPremiumType,
    );

    const insurerBranchOffice = getInsurerBranchOffice({
      formInsurer: insurerPartnerNumberByPremiumType,
      insurerList,
      formBranchOffice: selectedFleetInsurerDefaultSetting?.insurerBranchOffice,
    });
    setFieldValue("insurerPartnerNumber", selectedInsurer);

    if (selectedFleetInsurerDefaultSetting) {
      setFieldValue("insurerBranchOffice", insurerBranchOffice);
    }
    let fieldNames2 = [];

    if (selectedFleetInsurerDefaultSetting) {
      if (
        selectedFleetInsurerDefaultSetting.renewalDay &&
        selectedFleetInsurerDefaultSetting.renewalMonth &&
        values.validFromDate
      ) {
        const validFromDate = moment(values.validFromDate);
        const renewalDay = selectedFleetInsurerDefaultSetting.renewalDay
          .toString()
          .padStart(2, "0");
        const renewalMonth = selectedFleetInsurerDefaultSetting.renewalMonth
          .toString()
          .padStart(2, "0");

        // Construct the expiry date
        const expiryDate = moment(
          `${validFromDate.year() + 1}-${renewalMonth}-${renewalDay}`,
          "YYYY-MM-DD",
        );

        // If the constructed expiry date is before the valid from date, add one more year
        if (expiryDate.isAfter(moment(validFromDate).add(1, "year"))) {
          expiryDate.subtract(1, "year");
        }
        setFieldValue("expiryDate", expiryDate.toDate());
        setFieldTouched("expiryDate", false);
      }

      const paymentMean = paymentMeanOptions.find(
        (item) =>
          item.value === selectedFleetInsurerDefaultSetting?.paymentMean,
      );

      const paymentMethod = paymentMethodOptions.find(
        (item) =>
          item.value === selectedFleetInsurerDefaultSetting?.paymentMethod,
      );

      const paymentMeanFleetLegal = paymentMeanOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMeanFleetLegal,
      );

      const paymentMeanHullOnLand = paymentMeanOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMeanHullOnLand,
      );

      const paymentMeanMachineryBreakdown = paymentMeanOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMeanMachineryBreakdown,
      );

      const paymentMethodFleetLegal = paymentMethodOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMethodFleetLegal,
      );

      const paymentMethodHullOnLand = paymentMethodOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMethodHullOnLand,
      );

      const paymentMethodMachineryBreakdown = paymentMethodOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMethodMachineryBreakdown,
      );

      const paymentMethodEngineTax = paymentMethodOptions.find(
        (item) =>
          item.value ===
          selectedFleetInsurerDefaultSetting?.paymentMethodEngineTax,
      );

      if (isFleetLegalProRataPremiumType) {
        if (paymentMeanFleetLegal) {
          setFieldValue("paymentMean", paymentMeanFleetLegal);
        } else {
          setFieldValue("paymentMean", paymentMean);
        }
        if (paymentMethodFleetLegal) {
          setFieldValue("paymentMethod", paymentMethodFleetLegal);
        } else {
          setFieldValue("paymentMethod", paymentMethod);
        }
      } else if (isHullOnLandProRataPremiumType) {
        if (paymentMeanHullOnLand) {
          setFieldValue("paymentMean", paymentMeanHullOnLand);
        } else {
          setFieldValue("paymentMean", paymentMean);
        }
        if (paymentMethodHullOnLand) {
          setFieldValue("paymentMethod", paymentMethodHullOnLand);
        } else {
          setFieldValue("paymentMethod", paymentMethod);
        }
      } else if (isMachineryBreakdownProRataPremiumType) {
        if (paymentMeanMachineryBreakdown) {
          setFieldValue("paymentMean", paymentMeanMachineryBreakdown);
        } else {
          setFieldValue("paymentMean", paymentMean);
        }
        if (paymentMethodMachineryBreakdown) {
          setFieldValue("paymentMethod", paymentMethodMachineryBreakdown);
        } else {
          setFieldValue("paymentMethod", paymentMethod);
        }
      } else if (isHorsePowerPremiumType) {
        // Engine-tax line: its single payment-method field maps to the
        // insurer setting's engine-tax payment method.
        if (paymentMethodEngineTax) {
          setFieldValue("paymentMethod", paymentMethodEngineTax);
        } else {
          setFieldValue("paymentMethod", paymentMethod);
        }
      } else {
        if (paymentMean !== undefined) {
          setFieldValue("paymentMean", paymentMean);
        }
        if (paymentMethod !== undefined) {
          setFieldValue("paymentMethod", paymentMethod);
        }
      }

      setFieldTouched("motorTaxPaymentMethod", false);

      if (paymentMethodEngineTax) {
        setFieldValue("motorTaxPaymentMethod", paymentMethodEngineTax);
      } else {
        setFieldValue("motorTaxPaymentMethod", paymentMethod);
      }

      if (selectedFleetInsurerDefaultSetting?.activationDays?.toString()) {
        setFieldValue(
          "activationDays",
          selectedFleetInsurerDefaultSetting?.activationDays,
        );
      } else {
        fieldNames2.push("activationDays");
      }

      if (selectedFleetInsurerDefaultSetting?.suspensionDays?.toString()) {
        setFieldValue(
          "suspensionDays",
          selectedFleetInsurerDefaultSetting?.suspensionDays,
        );
      } else {
        fieldNames2.push("suspensionDays");
      }

      if (isHullPremium) {
        if (
          selectedFleetInsurerDefaultSetting?.garageRiskDiscountPercentHull?.toString()
        ) {
          setFieldValue(
            "garageRiskDiscountPercent",
            selectedFleetInsurerDefaultSetting?.garageRiskDiscountPercentHull
              ?.toString()
              .replace(".", ","),
          );
        } else {
          fieldNames2.push("garageRiskDiscountPercent");
        }
      } else {
        if (
          selectedFleetInsurerDefaultSetting?.garageRiskDiscountPercentHullBasic?.toString()
        ) {
          setFieldValue(
            "garageRiskDiscountPercent",
            selectedFleetInsurerDefaultSetting?.garageRiskDiscountPercentHullBasic
              ?.toString()
              .replace(".", ","),
          );
        } else {
          fieldNames2.push("garageRiskDiscountPercent");
        }
      }
    } else {
      fieldNames2.push("suspensionDays");
      fieldNames2.push("activationDays");
      fieldNames2.push("garageRiskDiscountPercent");
    }

    let fieldNames1 = ["annualGrossPremium", "deductible", "calculation"];

    dispatch(
      setDialogDefaultValueOperation({
        fieldNames: fieldNames1,
        formValues: values,
        noMessage: noToastMsg,
      }),
    );

    // Fleet per-premium-type suspension days limit takes precedence over insurer
    // settings when set (non-null); otherwise fall back to insurer settings.
    const fleetPerPremiumDetails =
      selectedFleetInsurerDefaultSetting?.fleetInsurerDefaultSettingPerPremiumTypeDetails;

    const effectivePremiumTypeId =
      isHullPremiumType && !isHullPremium
        ? taxonomy.PremiumType.byCode[PremiumType.HULL_BASIC]?.id
        : premiumTypeValue;

    const fleetSusp = fleetPerPremiumDetails?.find(
      (d) => d.premiumTypeCode === effectivePremiumTypeId,
    )?.suspensionDaysLimit;
    if (fleetSusp !== null && fleetSusp !== undefined) {
      setFieldValue("suspensionDaysLimit", fleetSusp + "");
    } else {
      fieldNames2.push("suspensionDaysLimit");
    }

    const horsePowerId =
      taxonomy.PremiumType.byCode[PremiumType.HORSE_POWER]?.id;
    const fleetMotorTax = fleetPerPremiumDetails?.find(
      (d) => d.premiumTypeCode === horsePowerId,
    )?.suspensionDaysLimit;
    if (fleetMotorTax !== null && fleetMotorTax !== undefined) {
      setFieldValue("motorTaxSuspensionDaysLimit", fleetMotorTax + "");
    } else {
      fieldNames2.push("motorTaxSuspensionDaysLimit");
    }

    fieldNames2.push("annualNetPremium");

    dispatch(
      setDialogInsurerSettingsOperation({
        fieldNames: fieldNames2,
        formValues: values,
        noMessage: noToastMsg,
      }),
    );
  };

  const copyFromOtherLine = (
    values: any,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const validFromDate = values["validFromDate"];
    if (!validFromDate) return;

    const otherPremiumTypeKey = isMtplPremiumType
      ? PremiumType.HULL
      : PremiumType.MTPL;
    const otherPremiumTypeId = premiumTypesTaxonomy[otherPremiumTypeKey]?.id;
    const otherPremiumTypeIdBasic = isMtplPremiumType
      ? premiumTypesTaxonomy[PremiumType.HULL_BASIC]?.id
      : undefined;

    const historyData = vehicle?.vehiclePremiumSegmentHistoryData;
    if (!historyData || otherPremiumTypeId === undefined) return;

    const checkDate = new Date(validFromDate);
    const activeSegment = historyData.find((seg) => {
      if (
        seg.premiumTypeCode !== otherPremiumTypeId &&
        seg.premiumTypeCode !== otherPremiumTypeIdBasic
      )
        return false;
      const from = new Date(seg.validFromDate);
      const to = seg.validToDate ? new Date(seg.validToDate) : null;
      return from <= checkDate && (to === null || to >= checkDate);
    });

    if (!activeSegment) return;

    setFieldValue(
      "insurerPolicyNumber",
      activeSegment.insurerPolicyNumber || "",
    );
    if (activeSegment.documentDate) {
      setFieldValue("documentDate", new Date(activeSegment.documentDate));
    }

    // Insurer
    if (activeSegment.insurerPartnerNumber != null) {
      const insurer = insurerList?.find(
        (i) => i.insurerInternalNumber === activeSegment.insurerPartnerNumber,
      );
      setFieldValue(
        "insurerPartnerNumber",
        insurer
          ? {
              value: insurer.insurerInternalNumber,
              label:
                insurer.insurerName +
                " (" +
                insurer.insurerInternalNumber +
                ")",
            }
          : null,
      );
    }

    // Branch office: the branch-office field resets itself to null when the
    // insurer changes, so defer setting it until after that reset effect has
    // run and its option list reflects the newly selected insurer.
    const branchOffice = getInsurerBranchOffice({
      formInsurer: activeSegment.insurerPartnerNumber,
      insurerList,
      formBranchOffice: activeSegment.insurerBranchOffice,
    });
    setTimeout(() => {
      setFieldValue("insurerBranchOffice", branchOffice);
    }, 0);
  };

  const onSubmit = (data) => {
    let result = transformForServer({
      obj: data,
      config: config,
    }) as any;
    result = removeEmpty(result);
    // CZ: a hidden expiry date (e.g. pre-filled from the renewal date) is not sent - the backend sets it
    if (
      !isPremiumExpiryDateShown(
        appSettings?.COUNTRY_CODE,
        data?.paymentMethod,
        getOneTimePaymentMethodId(taxonomy),
      )
    ) {
      delete result.expiryDate;
    }
    let premiumTypeKeyFinal = premiumTypeKey;

    const isClosingDate =
      data?.premiumType?.value ===
        taxonomy.PremiumType.byCode[PremiumType.FLEETLEGAL_CLOSINGDATE]?.id ||
      data?.premiumType?.value ===
        taxonomy.PremiumType.byCode[PremiumType.HULL_ON_LAND_CLOSINGDATE]?.id ||
      data?.premiumType?.value ===
        taxonomy.PremiumType.byCode[PremiumType.MACHINERY_BREAKDOWN_CLOSINGDATE]
          ?.id;

    if (
      premiumTypeKeyFinal === PremiumType.HULL &&
      data.hullIsBasic.value === "true"
    ) {
      premiumTypeKeyFinal = PremiumType.HULL_BASIC;
    }
    if (isSwitchOperation) {
      premiumTypeKeyFinal = switchTargetIsBasic
        ? PremiumType.HULL_BASIC
        : PremiumType.HULL;
    }

    let objectToSend = {
      vehicleId: vehicle.vehicleId as any,
      fleetId: vehicle.fleetId as any,
      body: {
        ...result,
        premiumType:
          data?.premiumType?.value ||
          taxonomy.PremiumType.byCode[`${premiumTypeKeyFinal}`]?.id,
        hullPremiumRelatedInfo: {
          hullProduct: result.hullProduct,
          hullDeductiblePercent: result.hullDeductiblePercent,
          hullDeductibleMaxAmount: result.hullDeductibleMaxAmount,
          hullDeductibleMinAmount: result.hullDeductibleMinAmount,
          hullDeductibleComment: result.hullDeductibleComment,
          gap: result.gap,
          replacementValue: result.replacementValue,
          grossNegligence: result.grossNegligence,
          electroPlusPackage: result.electroPlusPackage,
        },
      },
    };
    delete objectToSend.body.hullProduct;
    delete objectToSend.body.hullDeductiblePercent;
    delete objectToSend.body.hullDeductibleMaxAmount;
    delete objectToSend.body.hullDeductibleMinAmount;
    delete objectToSend.body.hullDeductibleComment;
    delete objectToSend.body.gap;
    delete objectToSend.body.replacementValue;
    delete objectToSend.body.grossNegligence;
    delete objectToSend.body.electroPlusPackage;

    if (objectToSend.body?.isMotorTaxIncluded === true) {
      objectToSend.body = {
        ...objectToSend.body,
        motorTaxExclusionReason: null,
      };
    }
    if (
      premiumTypeKey !== PremiumType.HULL &&
      premiumTypeKey !== PremiumType.HULL_BASIC
    ) {
      delete objectToSend.body.hullPremiumRelatedInfo;
    }

    if (isClosingDate) {
      objectToSend.body.isFirstForClosingDate = data.isFirstForClosingDate;
    }

    const mutationOptions = {
      onSuccess: (res) => {
        dispatch(setOperationRetVal(res?.data));
        toast.success(t("bfm.success"));
        dispatch(closeDialog());
      },
    };
    if (isSwitchOperation) {
      // cancelationDocumentDate and reasonForCancellingInsurance stay in the body for the cancelled premium
      onSwitchVehicleHullPremiumType(
        { rowVersion, params: objectToSend },
        mutationOptions,
      );
    } else if (isInsertOperation) {
      onInsertVehiclePremium(
        { rowVersion, params: objectToSend },
        mutationOptions,
      );
    } else {
      onAddVehiclePremium({ rowVersion, params: objectToSend }, mutationOptions);
    }
  };

  const Title = (
    <Flex>
      <Icon
        iconName={isSwitchOperation ? "Switch" : "CircleAddition"}
        style={{
          color: theme.palette.green,
        }}
      />
      <HSpace />
      <div>
        {isSwitchOperation
          ? t(
              switchTargetIsBasic
                ? "bfm.convertToHullBasic"
                : "bfm.convertToHull",
            )
          : (isInsertOperation
              ? t("bfm.insertPremium")
              : t("bfm.addPremium")) +
            " " +
            t(`${premiumTypeKey}`)}
      </div>
    </Flex>
  );

  // CZ: a new MTPL / Hull premium starts on the client's requested coverage start, if there is one
  const requestedCoverageFrom =
    appSettings?.COUNTRY_CODE === "Country.CZ" &&
    !isInsertOperation &&
    !isSwitchOperation &&
    (isMtplPremiumType || isHullPremiumType)
      ? getRequestedCoverageFrom(
          vehicle?.clientCoveragesInfo,
          isMtplPremiumType ? "mtpl" : "hull",
        )
      : undefined;
  const validFromRaw =
    requestedCoverageFrom ??
    (hasPremiumHistory ? date : vehicleRegistrationDate);
  const validFromDate = moment(validFromRaw).toDate();
  const momentDate = moment(date).toDate();

  const initialValues = useMemo(() => {
    if (isSwitchOperation && item?.associatedObject) {
      // Hull <-> Hull-Basic conversion: prefill from the converted premium (incl. its additional covers);
      // premium amount, deductible and garage risk are then taken from the target type's default settings
      // (see AddPremiumDialogContent)
      const converted = item.associatedObject;
      const hullInfo = converted.hullPremiumRelatedInfo || {};
      // additional covers are copied 1:1, incl. "not defined" (a default-setting surcharge only applies once a cover is set to yes)
      const carriedCovers = {
        gap: hullInfo.gap ?? null,
        replacementValue: hullInfo.replacementValue ?? null,
        grossNegligence: hullInfo.grossNegligence ?? null,
        electroPlusPackage: hullInfo.electroPlusPackage ?? null,
      };
      const convertedDueDay = converted.dueDay ?? dueDay;
      const convertedDueMonth = converted.dueMonth ?? dueMonth;

      const obj: any = {
        insurerPartnerNumber: converted.insurerPartnerNumber,
        insurerBranchOffice: converted.insurerBranchOffice,
        // the new hull premium continues under the same policy as the converted one
        insurerPolicyNumber: converted.insurerPolicyNumber,
        insurerGroupPolicyNumber: converted.insurerGroupPolicyNumber,
        isPolicyIssued: converted.isPolicyIssued ?? false,
        paymentMethod: converted.paymentMethod,
        paymentMean: converted.paymentMean,
        suspensionDays: converted.suspensionDays,
        activationDays: converted.activationDays,
        suspensionDaysLimit: converted.suspensionDaysLimit,
        garageRiskDiscountPercent: converted.garageRiskDiscountPercent,
        validFromDate: momentDate,
        // both document dates are pre-set to the conversion date and stay editable
        documentDate: momentDate,
        cancelationDocumentDate: momentDate,
        expiryDate: new Date(
          momentDate.getFullYear() + 1,
          Number(convertedDueMonth) - 1,
          Number(convertedDueDay),
        ),
        validToDate: item.endDate,
        annualGrossPremium: 0,
        hullIsBasic: switchTargetIsBasic,
        // Hull is always Vollkasko; for Hull-Basic the user picks Teilkasko or Parkschadenkasko
        hullProduct: switchTargetIsBasic ? null : "Vollkasko",
        // deductible is not copied: it comes from the target hull type's default settings or is entered by the user
        hullDeductiblePercent: null,
        hullDeductibleMinAmount: null,
        hullDeductibleMaxAmount: null,
        hullDeductibleComment: null,
        hullAdditionalCover: hullInfo.hullAdditionalCover,
        ...carriedCovers,
        dueDate:
          ((convertedDueDay + "").length === 1
            ? "0" + convertedDueDay
            : convertedDueDay) +
          "" +
          ((convertedDueMonth + "").length === 1
            ? "0" + convertedDueMonth
            : convertedDueMonth),
        reasonForCancellingInsurance: "",
        reasonForModificationComment: "",
      };
      const switchInitObj: any = transformForForm({
        obj,
        config,
        t,
        taxonomy,
        insurerList,
        possibleMainVehicles: null,
      });
      return {
        ...switchInitObj,
        // use the option values of the (locked) hull type combobox
        hullIsBasic: switchTargetIsBasic
          ? { value: "true", label: t("bfm.vehicleForm.hullType.basic.label") }
          : { value: "false", label: t("bfm.vehicleForm.hullType.full.label") },
        validToDate: item.endDate,
        policyStatus: "",
      };
    }

    const insurerInternalNumberByPremiumType = (() => {
      if (isMtplPremiumType) {
        return Number(mainMtplInsurerPartnerNumber);
      }
      if (isHullPremiumType) {
        return Number(mainHullInsurerPartnerNumber);
      }
      if (isFleetLegalProRataPremiumType) {
        return Number(mainFleetLegalInsurerPartnerNumber);
      }

      return null;
    })();

    const insurerPartnerNumberByPremiumType = insurerList.find(
      (insurer) =>
        insurer.insurerInternalNumber === insurerInternalNumberByPremiumType,
    )?.insurerInternalNumber;

    const expiryDate = new Date(
      validFromDate.getFullYear() + 1,
      Number(dueMonth) - 1, // if month is 1 that is february
      Number(dueDay),
    );
    let obj: any = {
      insurerPartnerNumber: insurerPartnerNumber,
      validFromDate,
      expiryDate,
      validToDate: item.endDate,
      annualGrossPremium: 0,
      isPolicyIssued: false,
    };

    if (
      isMtplPremiumType ||
      isHullPremiumType ||
      isFleetLegalProRataPremiumType
    ) {
      obj = {
        ...obj,
        insurerPartnerNumber: insurerPartnerNumberByPremiumType,
      };
    }

    if (isHullPremiumType) {
      obj = {
        ...obj,
        hullIsBasic: false,
        gap: null,
        replacementValue: null,
        grossNegligence: null,
        electroPlusPackage: null,
      };
    }

    if (
      premiumTypeKey === PremiumType.MTPL ||
      premiumTypeKey === PremiumType.HULL ||
      premiumTypeKey === PremiumType.HULL_BASIC ||
      //debugger
      isFleetLegalProRataPremiumType ||
      isFleetLegalClosingDatePremiumType ||
      isMachineryBreakdownProRataPremiumType ||
      isMachineryBreakdownClosingDatePremiumType ||
      isHullOnLandProRataPremiumType ||
      isHullOnLandClosingDatePremiumType
      //debugger
    ) {
      obj = {
        ...obj,
        insurerPolicyNumber:
          isFleetLegalProRataPremiumType || isFleetLegalClosingDatePremiumType
            ? fleetLegalPolicyNumber
            : isMachineryBreakdownProRataPremiumType ||
                isMachineryBreakdownClosingDatePremiumType
              ? machineryBreakdownPolicyNumber
              : isHullOnLandProRataPremiumType ||
                  isHullOnLandClosingDatePremiumType
                ? hullOnLandPolicyNumber
                : fleetPolicyNumber,
        dueDate:
          ((dueDay + "").length === 1 ? "0" + dueDay : dueDay) +
          "" +
          ((dueMonth + "").length === 1 ? "0" + dueMonth : dueMonth),
      };
    }

    if (appSettings?.INCLUDE_GROUP_POLICY) {
      obj = {
        ...obj,
        groupPolicyNumber: null,
      };
    }
    if (
      isFleetLegalProRataPremiumType ||
      isMachineryBreakdownProRataPremiumType ||
      isHullOnLandProRataPremiumType
    ) {
      obj = {
        ...obj,
        suspensionDaysLimit: "-1",
      };
    }

    if (
      isFleetLegalClosingDatePremiumType ||
      isMachineryBreakdownClosingDatePremiumType ||
      isHullOnLandClosingDatePremiumType
    ) {
      obj = {
        ...obj,
        isFirstForClosingDate: null,
      };
    }
    let initObj = transformForForm({
      obj: obj,
      config: config,
      t,
      taxonomy,
      insurerList,
      possibleMainVehicles: null,
    });

    initObj = {
      ...initObj,

      validToDate: item.endDate,
    };
    if (
      premiumTypeKey === PremiumType.MTPL &&
      appSettings?.VEHICLE_PREMIUM_DOMAINS.includes(PremiumType.HORSE_POWER)
    ) {
      initObj = {
        ...initObj,
        isMotorTaxIncluded: true,
        motorTaxSuspensionDaysLimit: "45",
      };
    }
    const suspensionDaysIncluded = appSettings?.SUSPENSION_DAYS_INCLUDED;
    const suspensionDaysDefault = appSettings?.SUSPENSION_DAYS_DEFAULT;
    const activationDaysIncluded = appSettings?.ACTIVATION_DAYS_INCLUDED;
    const activationDaysDefault = appSettings?.ACTIVATION_DAYS_DEFAULT;
    const suspensionDaysLimitIncluded =
      appSettings?.SUSPENSION_DAYS_LIMIT_INCLUDED;
    const suspensionDaysLimitDefault =
      appSettings?.SUSPENSION_DAYS_LIMIT_DEFAULT;
    const garageRiskDiscountPercentIncluded =
      appSettings?.GARAGE_RISK_DISCOUNT_PERCENT_INCLUDED;
    const garageRiskDiscountPercentDefault =
      appSettings?.GARAGE_RISK_DISCOUNT_PERCENT_DEFAULT;

    if (!suspensionDaysIncluded) {
      initObj = {
        ...initObj,
        suspensionDays: suspensionDaysDefault + "",
      };
    }
    if (!activationDaysIncluded) {
      initObj = {
        ...initObj,
        activationDays: activationDaysDefault + "",
      };
    }
    if (!suspensionDaysLimitIncluded) {
      initObj = {
        ...initObj,
        suspensionDaysLimit: suspensionDaysLimitDefault,
      };
    }
    if (!garageRiskDiscountPercentIncluded) {
      initObj = {
        ...initObj,
        garageRiskDiscountPercent: garageRiskDiscountPercentDefault + "",
      };
    }
    initObj = {
      ...initObj,
      policyStatus: "",
    };

    // if (
    //   AppSettings.getInstance()
    //     .getSettings()
    //     .VEHICLE_TIMELINE_TYPES.includes(TimelineType.VEHICLE_POLICY_STATUSES)
    // ) {
    //   initObj = {
    //     ...initObj,
    //     policyStatus: {
    //       label: t("VehiclePolicyStatusCode.PENDING"),
    //       value:
    //         taxonomy.VehiclePolicyStatusCode.byCode[
    //           "VehiclePolicyStatusCode.PENDING"
    //         ].id,
    //     },
    //   };
    // }

    return initObj;
  }, [
    t,
    premiumTypeKey,
    taxonomy,
    insurerList,
    insurerPartnerNumber,
    item,
    momentDate,
    dueDay,
    dueMonth,
    vehicleRegistrationDate,
    hasPremiumHistory,
    vehicle?.clientCoveragesInfo,
    mainMtplInsurerPartnerNumber,
    isSwitchOperation,
    switchTargetIsBasic,
  ]);

  return (
    <Dialog
      hidden={false}
      dialogContentProps={{
        title: Title,
        showCloseButton: true,
      }}
      minWidth={
        (premiumTypeKey === PremiumType.MTPL &&
          appSettings?.VEHICLE_PREMIUM_DOMAINS.includes(
            PremiumType.HORSE_POWER,
          )) ||
        premiumTypeKey === PremiumType.HULL
          ? 1050
          : timelineAppSettings?.HAS_ANNUAL_PREMIUM_EUR
            ? 900
            : 700
      }
      onDismiss={() => {
        dispatch(closeDialog());
      }}
      modalProps={{
        isBlocking: phraseActivated ? false : true,
      }}
    >
      <RefProvider>
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          enableReinitialize
          validateOnChange={didTrySubmit}
          validateOnBlur={didTrySubmit}
          validationSchema={schema}
        >
          {({ submitForm, values, setFieldValue, setFieldTouched }) => {
            return (
              <Form>
                <AddPremiumDialogContent
                  item={item}
                  premiumTypeKey={premiumTypeKey}
                  setDefaultSettingValues={setDefaultSettingValues}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", gap: "8px" }}>
                    <TooltipHost content={t("bfm.fillInAllDefaults.label")}>
                      <StyledPrimaryButton
                        text={t("bfm.defaultSettings.label")}
                        onClick={() => {
                          // Only an explicit basic selection is basic hull (as
                          // in onSubmit); a cleared field means full hull.
                          const isHullPremium =
                            values["hullIsBasic"]?.value !== "true";

                          setDefaultSettingValues({
                            setFieldValue,
                            values,
                            insurer: values["insurerPartnerNumber"],
                            mustHaveInsurer: true,
                            noToastMsg: false,
                            isHullPremium,
                            setFieldTouched,
                          });
                        }}
                        iconProps={{
                          iconName: "Shield",
                        }}
                        disabled={
                          isLoading ||
                          !values["insurerPartnerNumber"] ||
                          !values["validFromDate"]
                        }
                      />
                    </TooltipHost>
                    {isAustria && (isMtplPremiumType || isHullPremiumType) && (
                      <TooltipHost
                        content={t(
                          isMtplPremiumType
                            ? "bfm.copyDataFromHull.tooltip"
                            : "bfm.copyDataFromMtpl.tooltip",
                        )}
                      >
                        <StyledPrimaryButton
                          text={t(
                            isMtplPremiumType
                              ? "bfm.copyDataFromHull.label"
                              : "bfm.copyDataFromMtpl.label",
                          )}
                          onClick={() =>
                            copyFromOtherLine(values, setFieldValue)
                          }
                          iconProps={{ iconName: "Copy" }}
                          disabled={isLoading || !values["validFromDate"]}
                        />
                      </TooltipHost>
                    )}
                  </div>

                  <StyledPrimaryButton
                    text={t("greco.save")}
                    onClick={() => {
                      setDidTrySubmit(true);
                      submitForm();
                    }}
                    iconProps={{
                      iconName: "Save",
                    }}
                    disabled={isLoading}
                  />
                </div>
                {isLoading && <FormSpinner />}
              </Form>
            );
          }}
        </Formik>
      </RefProvider>
    </Dialog>
  );
};
